import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import ValidatorAccountForm from 'src/features/validatorAccounts/components/forms/ValidatorAccountForm';
import {
    validatorAccountFormSchema,
    ValidatorAccountFormSchemaValues,
} from 'src/features/validatorAccounts/utils/formSchema';
import { ValidatorAccount } from 'src/features/validatorAccounts/utils/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface ValidatorAccountFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAccount?: ValidatorAccount;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: ValidatorAccountFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function ValidatorAccountFormModal<TData>({
    selectedAccount,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: ValidatorAccountFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            name: selectedAccount?.name ?? undefined,
            description: selectedAccount?.description ?? undefined,
            users: selectedAccount?.users?.map((u) => u.id) ?? undefined,
            cloud_users: selectedAccount?.cloud_users?.map((u) => u.id) ?? undefined,
        };
    }, [selectedAccount]);

    const form = useForm<ValidatorAccountFormSchemaValues>({
        resolver: zodResolver(validatorAccountFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [selectedAccount, isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: ValidatorAccountFormSchemaValues) => {
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },

        [mutate, form, onError],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ValidatorAccountForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedAccount && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ValidatorAccountFormModal;
